import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store from './store';
import EveryDoggyAPIServices from './utils/services/EveryDoggyAPIServices';
import appErrorLogger from './utils/errorLogger/appErrorLogger';
import { initAmplitude } from './analytics/amplitude';
import { initGoogleAds } from './analytics/initGoogleAds';
import { initTikTokPixel } from './analytics/initTikTokPixel';
import { Environment } from './types';
import App from './App';
import { getParameterFromURL } from './utils/scripts';
import { isK9GreenThemeConfig, K9_GREEN_THEME_CLASS } from './utils/scripts/k9Funnel';

import './index.scss';

// Applied before the first render so modals/overlays never flash the default theme.
if (isK9GreenThemeConfig(getParameterFromURL('config'))) {
  document.documentElement.classList.add(K9_GREEN_THEME_CLASS);
}

window.dataLayer = window.dataLayer || [];
initAmplitude();
initTikTokPixel();
initGoogleAds();

function loadPaypalSDK() {
  EveryDoggyAPIServices.getPaypalConfig()
    .then(data => {
      // Config unavailable (network drop, adblocker) — skip PayPal instead of
      // crashing on `null.client_id`; the payment viewModels retry on their own.
      if (!data?.client_id) return;
      // Load paypal
      const script = document.createElement('script');
      script.setAttribute(
        'src',
        `https://www.paypal.com/sdk/js?client-id=${data.client_id}&vault=true&intent=subscription&disable-funding=card`
      );
      script.setAttribute('async', 'false');
      script.setAttribute('data-sdk-integration-source', 'button-factory');
      const head = document.head;
      head.insertBefore(script, head.firstChild);
    })
    .catch(() => {
      // getPaypalConfig resolves null on failure; this only guards against an
      // unexpected rejection becoming an unhandled promise rejection.
    });
}

// In-app WebViews (Facebook/Snapchat/etc.) often kill requests fired while the
// page is still backgrounded during the open-link handoff — defer the SDK load
// until the page is actually visible.
if (document.visibilityState === 'hidden') {
  document.addEventListener('visibilitychange', function onVisible() {
    if (document.visibilityState === 'visible') {
      document.removeEventListener('visibilitychange', onVisible);
      loadPaypalSDK();
    }
  });
} else {
  loadPaypalSDK();
}

// setup and initialize error tracking.
// Production only: without Sentry.init no client exists, so captureException /
// captureMessage become no-ops and the auto-instrumented handlers (onerror,
// onunhandledrejection) and BrowserTracing are never installed. That is the only
// way to silence every channel at once — the per-call ENVIRONMENT guards cover
// EveryDoggyAPIServices, but not capturePaymentError, errorLogger or traces.
if (process.env.ENVIRONMENT === Environment.prod) {
  appErrorLogger.initialize();
}

const root = createRoot(document.getElementById('root'));
root.render(
  <Provider store={store}>
    <App />
  </Provider>
);
